import React, { useState } from 'react';
import { CandidateState } from './types';
import { Box, IconButton, Popover, TableCell, Stack } from '@mui/material';
import { Circle as CircleIcon, AttachFile as AttachFileIcon } from '@mui/icons-material';
import { classes } from '../../NewUI/Components/GenericTable/styles';
import { ICandidateApplication } from './types';
import dayjs from 'dayjs';
import { styles } from './styles';
import { getRatingColor } from '../Job/Applications/SelectedApplicants';
import DocTypeIcon from '../Components/Utilities/DocTypeIcon';
import FilePreviewModal, { IJobAttachment } from '../Components/Modals/FilePreviewModal';
import { formatUserPermissions } from '../Components/Utilities/formatUserPermissions';

const CandidateApplicationsTableRows = ({
  column,
  item,
  tableHovered
}: {
  column: CandidateState['candidateApplicationsTableState']['columns'][number];
  item: ICandidateApplication;
  tableHovered: boolean;
}) => {
  const userPermissions = formatUserPermissions();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<IJobAttachment | null>(null);
  const handleSelectAttachment = (attachment: IJobAttachment) => {
    setSelectedAttachment({ ...attachment, application_link: item.application_link });
    setPreviewOpen(true);
  };

  if (!column.enabled) return null;
  switch (column.id) {
    case 'id':
      return (
        <TableCell
          key={item.id + column.id}
          sx={{
            ...classes.sticky,
            ...classes.idTableCell,
            minWidth: '100px',
            left: '0px',
            ...styles.candidateApplicationsTableLink
          }}
        >
          <a
            href={item.sister_entity_application ? undefined : item.application_link}
            id="application-id-link"
          >
            {item.id || '-'}
          </a>
        </TableCell>
      );
    case 'job':
      return (
        <TableCell
          key={item.id + column.id}
          sx={{
            ...classes.sticky,
            ...classes.itemTitleTableCell,
            ...(tableHovered && classes.scrollShadowVisible),
            left: '100px',
            minWidth: '150px',
            ...styles.candidateApplicationsTableLink
          }}
        >
          <a
            href={item.sister_entity_application ? undefined : item.application_link}
            id="application-job-name-link"
          >
            {item.job_title || '-'}
          </a>
        </TableCell>
      );
    case 'entity':
      return (
        <TableCell key={item.id + column.id} sx={styles.candidateApplicationsTableLink}>
          <a
            href={item.sister_entity_application ? undefined : item.entity_link}
            id="entity-name-link"
          >
            {item.entity || '-'}
          </a>
        </TableCell>
      );
    case 'created_at':
      return (
        <TableCell key={item.id + column.id}>
          {dayjs(item.created_at).format('D MMM YYYY, LT') || '-'}
        </TableCell>
      );
    case 'rating':
      return (
        <TableCell key={item.id + column.id}>
          {item.rating ? (
            <Stack
              sx={{
                ...styles.candidateApplicationsRating,
                background: getRatingColor(Number(item.rating))
              }}
            >
              {item.rating.replace('.0', '')}%
            </Stack>
          ) : (
            '-'
          )}
        </TableCell>
      );
    case 'status':
      return (
        <TableCell key={item.id + column.id}>
          {item.status ? (
            <Stack sx={styles.candidateApplicationsTableStatusContainer}>
              <CircleIcon sx={{ fontSize: '0.5rem', color: item.status_colour || '#808080' }} />
              {item.status}
            </Stack>
          ) : (
            '-'
          )}
        </TableCell>
      );
    case 'attachments':
      return (
        <TableCell key={item.id + column.id}>
          {item.attachments.length ? (
            <Stack sx={{ flexDirection: 'row', justifyContent: 'end' }}>
              <IconButton
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                  '&:focus': {
                    outline: 'none'
                  }
                }}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </Stack>
          ) : null}
          <Popover
            id={anchorEl ? 'applicant-attachments' : undefined}
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <Box>
              {item.attachments?.map((attachment) => (
                <Box
                  key={attachment.id}
                  sx={classes.attachmentItem}
                  onClick={() => handleSelectAttachment(attachment)}
                >
                  <Box sx={classes.fileIcon}>
                    {DocTypeIcon(attachment.attached_file_name.split('.').pop())}
                  </Box>
                  <Box sx={classes.fileName}>{attachment.attached_file_name}</Box>
                </Box>
              ))}
            </Box>
          </Popover>
          <FilePreviewModal
            applicationLink={selectedAttachment?.application_link}
            isShareable={false}
            allowDownload={userPermissions?.['Applications']?.['Download Attachments']}
            {...{ previewOpen, setPreviewOpen, selectedAttachment }}
          />
        </TableCell>
      );
    default:
      return <TableCell></TableCell>;
  }
};

export default CandidateApplicationsTableRows;
